import { gql } from '@apollo/client';

/** Steps of one AI Search, in order (backend Enums::AiSearchStepEnum). New ones may appear. */
export enum AiSearchStep {
  ACCEPTED = 'ACCEPTED',
  UNDERSTANDING = 'UNDERSTANDING',
  WRITING_QUERY = 'WRITING_QUERY',
  RETRYING = 'RETRYING',
  RUNNING_QUERY = 'RUNNING_QUERY',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

/** A page the agent pointed to (web: features/ai-search/lib/pageLinks), by id; never a URL. */
export type AiSearchPage = { id: string; tab: string | null };

/** One of the patients a name in the question matched. */
export type AiSearchPatientChoice = { truentityId: string; name: string | null; birthDate: string | null };

export type AiSearchResult = {
  /** Identifies this search, for feedback on its answer. */
  searchId: string;
  /** `query` (data returned), `patients` (pick one of patientChoices) or `none` (text answer only). */
  intent: string;
  messages: string[];
  /** Questions to offer as clickable prompts. */
  suggestions: string[];
  query: string | null;
  rootField: string | null;
  data: Record<string, unknown> | null;
  variables: Record<string, unknown> | null;
  viewAllPayload: Record<string, unknown> | null;
  /** The page the agent named as listing all of what was asked. */
  page: AiSearchPage | null;
  /** The patients a name in the question matched, to pick from (intent `patients`). */
  patientChoices: AiSearchPatientChoice[] | null;
  /** Asked with the chosen patient (intent `patients`), e.g. "Show alerts for this patient". */
  followUpQuestion: string | null;
};

export type AiSearchEvent = {
  searchId: string;
  /** Increases within a search. */
  seq: number;
  step: AiSearchStep | string;
  status: 'STARTED' | 'DONE';
  message: string | null;
  detail: Record<string, unknown> | null;
  result: AiSearchResult | null;
  /** On FAILED: why there is no answer. */
  error: string | null;
  /** On COMPLETED and FAILED: questions to offer as clickable prompts. */
  suggestions: string[] | null;
};

export const AI_SEARCH = gql`
  query AiSearch($question: String!, $truentityId: String) {
    aiSearch(question: $question, truentityId: $truentityId) {
      searchId
      intent
      messages
      suggestions
      query
      rootField
      data
      variables
      viewAllPayload
      page {
        id
        tab
      }
      patientChoices {
        truentityId
        name
        birthDate
      }
      followUpQuestion
    }
  }
`;

export type AiSearchResponse = {
  aiSearch: AiSearchResult | null;
};

export const SUBMIT_AI_SEARCH_FEEDBACK = gql`
  mutation SubmitAiSearchFeedback($searchId: ID!, $thumbsUp: Boolean!, $question: String!, $comment: String) {
    submitAiSearchFeedback(searchId: $searchId, thumbsUp: $thumbsUp, question: $question, comment: $comment) {
      status
      feedbackId
    }
  }
`;

export type SubmitAiSearchFeedbackResponse = {
  submitAiSearchFeedback: { status: string; feedbackId: string };
};
